import { Localized, useLocalization } from "@fluent/react";
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Tab, TableContainer, TextField } from "@mui/material"
import { useCallback, useEffect, useState } from "react";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { loadUsersWithReleaseNotification, publishRelease, UsersWithReleaseNotification } from "./ReleasesApi";
import { unwrapResult } from "@reduxjs/toolkit";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { selectSettings } from "../../settings/settingsSlice";
import { TabContext, TabList, TabPanel } from "@mui/lab";

export interface PublishReleaseDialogProps {
    id: string;
    onCancel: () => void
}

export const PublishReleaseDialog: React.FC<PublishReleaseDialogProps> = ({ id, onCancel }) => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings);
    const dispatch = useAppDispatch()
    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")
    const [publisherName, setPublisherName] = useState<string>("");
    const [additionalAddresses, setAdditionalAddresses] = useState<string>("");
    const [users, setUsers] = useState<UsersWithReleaseNotification[]>([]);

    const [tab, setTab] = useState<"polish" | "english">("polish");

    useEffect(() => {
        dispatch(loadUsersWithReleaseNotification())
        .then(unwrapResult)
        .then(setUsers);
    }, [id])

    const handlePublisherNameChange = useCallback(e => {
        setPublisherName(e.target.value)
    }, [])

    const handleAdditionalAddressesChange = useCallback(e => {
        setAdditionalAddresses(e.target.value)
    }, [])

    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])

    const handleSaveClick = async () => {
        setSaving(true)

        try {
            const result = await dispatch(publishRelease({ id, publisherName, additionalAddresses }))
            if (publishRelease.fulfilled.match(result)) {
                dispatch(showSuccess("saved"))
            } else {
                let errorMsg = "error"
                if (result.payload && result.payload.kind === 'http') {
                    const problem = result.payload.problem
                    if (problem) {
                        errorMsg = problem.title

                    }
                }
                setServerError(errorMsg)
            }
        }
        catch (error) {
            dispatch(showError("error"));
        }
        finally {
            setSaving(false)
        }
    }

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const columns: GridColDef<UsersWithReleaseNotification, any, any>[] = [
        {
            field: 'userName',
            headerName: l10n.getString("username", null, "_Użytkownik"),
            flex: 1
        },
        {
            field: 'companyName',
            headerName: l10n.getString("company", null, "_Firma"),
            flex: 1
        }
    ]

    return <>
        <Dialog open={true} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Localized id="releases-publish-question">_Czy na pewno chcesz opublikować?</Localized>
            </DialogTitle>
            <DialogContent>
                <Localized id="releases-publish-message">_Wiadomość zostanie wysłana do klientów aplikacji</Localized>

                <TabContext value={tab}>
                    <TabList onChange={handleTabChange}>
                        <Tab
                            key={0}
                            value={"polish"}
                            label={<Localized id="polish">_Polski</Localized>} />
                        <Tab
                            key={1}
                            value={"english"}
                            label={<Localized id="english">_Angielski</Localized>} />
                    </TabList>
                    <TabPanel value="polish">
                        <TableContainer component={Paper}>
                            <DataGrid localeText={getCustomLocaleText()}
                                style={{ minHeight: 100, height: '250px' }}
                                rows={users.filter(f => f.language === "PL")}
                                columns={columns}
                                getRowId={(row) => row.companyName}
                            />
                        </TableContainer>
                    </TabPanel>
                    <TabPanel value="english">
                        <TableContainer component={Paper}>
                            <DataGrid localeText={getCustomLocaleText()}
                                style={{ minHeight: 100, height: '250px' }}
                                rows={users.filter(f => f.language === "EN")}
                                columns={columns}
                                getRowId={(row) => row.companyName}
                            />
                        </TableContainer>
                    </TabPanel>
                </TabContext>

                <Stack direction="column" spacing={2} padding={2}>
                    <TextField 
                        value={additionalAddresses}
                        fullWidth
                        onChange={handleAdditionalAddressesChange}                         
                        label={<Localized id="releases-additional-addresses">_Dodatkowe adresy e-mail (oddziel ";")</Localized>} />

                    <TextField 
                        required                            
                        value={publisherName}
                        fullWidth
                        onChange={handlePublisherNameChange}                         
                        label={<Localized id="releases-publisher">_Podpis osoby publikującej</Localized>} />
                </Stack>
                {
                    serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                }
            </DialogContent>
            <DialogActions>
                <SaveButton onClick={handleSaveClick} loading={saving} />
                <CancelButton onClick={onCancel} loading={saving} />
            </DialogActions>
        </Dialog>
    </>
}

